import React, { useState, useEffect } from 'react';
import lcaClient from '../../services/lcaClient';
const api = lcaClient;
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { 
  Lock, Unlock, Play, BarChart3, Calculator, AlertTriangle, FileCheck, Leaf, Droplets, Wind, Flame, Mountain
} from 'lucide-react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from 'recharts';

const COLORS = ['#0284c7', '#0891b2', '#d97706', '#c2410c', '#059669', '#7c3aed', '#db2777', '#65a30d'];

// Impact category icons
const categoryIcons = {
  climate_change: Leaf,
  acidification: Wind,
  eutrophication: Droplets,
  freshwater_eutrophication: Droplets,
  marine_eutrophication: Droplets,
  eutrophication_freshwater: Droplets,
  eutrophication_marine: Droplets,
  eutrophication_terrestrial: Droplets,
  photochemical_ozone: Wind,
  fossil_depletion: Flame,
  mineral_depletion: Mountain,
  abiotic_depletion_fossil: Flame,
  abiotic_depletion_minerals: Mountain,
  resource_use_fossils: Flame,
  resource_use_minerals: Mountain,
  water_consumption: Droplets,
  water_use: Droplets,
  human_toxicity_cancer: AlertTriangle,
  human_toxicity_non_cancer: AlertTriangle,
  freshwater_ecotoxicity: Droplets,
  ecotoxicity_freshwater: Droplets,
  land_use: Mountain,
  ionising_radiation: AlertTriangle,
  ozone_depletion: Wind,
  particulate_matter: Wind,
  // Ecological Scarcity 2021 (UBP) sub-indicators use the default icon
  total: BarChart3
};

export default function Phase3LCIA({ project, onUpdate, isLocked }) {
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [lciaResult, setLciaResult] = useState(null);
  const [methods, setMethods] = useState({});
  const [method, setMethod] = useState('IPCC_GWP_100');
  const [coverage, setCoverage] = useState(null);
  const [requireFullCoverage, setRequireFullCoverage] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('climate_change');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchData();
  }, [project.id]);

  const fetchData = async () => {
    try {
      const [resultRes, methodsRes, coverageRes] = await Promise.all([
        api.get(`/projects/${project.id}/lcia`).catch(() => ({ data: null })),
        api.get('/lcia/methods'),
        api.get(`/projects/${project.id}/lcia/coverage`).catch(() => ({ data: null }))
      ]);
      setLciaResult(resultRes.data);
      setMethods(methodsRes.data);
      setCoverage(coverageRes.data);

      if (resultRes.data?.method) {
        setMethod(resultRes.data.method);
      }
    } catch (error) {
      console.error('Failed to load LCIA data', error);
    } finally {
      setLoading(false);
    }
  };

  const runLCIA = async () => {
    if (project.phase_statuses['2'] !== 'locked') {
      toast.error('Phase 2 must be locked before running LCIA');
      return;
    }

    setRunning(true);
    try {
      const response = await api.post(`/projects/${project.id}/lcia/run`, {
        method,
        method_version: methods[method]?.version || 'v1.0',
        include_normalization: false,
        include_weighting: false,
        require_full_coverage: requireFullCoverage
      });
      setLciaResult(response.data);
      setSelectedCategory(methods[method]?.primary_indicator || 'climate_change');
      toast.success('LCIA calculation complete');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'LCIA calculation failed');
    } finally {
      setRunning(false);
    }
  };

  const handleUnlock = async () => {
    if (!window.confirm('Unlocking Phase 3 will clear the LCIA results and reset Phase 4 (Interpretation) to draft status. AI insights will be deleted. Do you want to continue?')) {
      return;
    }

    setUnlocking(true);
    try {
      await api.post(`/projects/${project.id}/lcia/unlock`);
      setLciaResult(null);
      toast.success('Phase 3 unlocked. You can now re-run LCIA calculations.');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock phase');
    } finally {
      setUnlocking(false);
    }
  };

  // Get impact categories from result
  const impactCategories = lciaResult?.impact_categories || {};
  const resultMethodMeta = methods[lciaResult?.method] || {};
  // Ecological Scarcity 2021: pre-weighted single score (UBP). The 'total'
  // gets a hero card; sub-indicators are listed without it (no double count)
  // and the normalized radar is replaced by a comparable-units bar chart.
  const isSingleScore = resultMethodMeta.method_type === 'single_score_weighted'
    || lciaResult?.impact_unit === 'UBP';
  const categoryList = Object.entries(impactCategories)
    .filter(([catId]) => !(isSingleScore && catId === 'total'));
  const totalCategory = impactCategories.total;

  // A single-score method's aggregate indicator (e.g. UBP 'total') is the
  // headline score, not an impact category. Exclude it so the method dropdown,
  // the category-preview chips, and the result metadata all report the same
  // count — the sub-indicators only.
  const methodCategoryKeys = (m) =>
    (m.categories || []).filter(
      (catId) => !(m.method_type === 'single_score_weighted' && catId === m.primary_indicator)
    );

  // Current selected category data
  const currentCategory = impactCategories[selectedCategory];

  const getSubIndicatorData = () => {
    return categoryList
      .map(([id, cat]) => ({
        name: cat.name,
        value: parseFloat((cat.total_value || 0).toFixed(2)),
      }))
      .filter(d => d.value !== 0)
      .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
  };

  const selectedMethodCoverage = coverage?.[method];
  const resultCoverage = lciaResult?.coverage;

  // Prepare chart data for selected category
  const getInventoryCategoryData = () => {
    if (!currentCategory) return [];
    return Object.entries(currentCategory.by_inventory_category || {})
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({
        name: name.toUpperCase(),
        value: parseFloat(value.toFixed(4)),
        percentage: currentCategory.total_value > 0 
          ? ((value / currentCategory.total_value) * 100).toFixed(1) 
          : 0
      }))
      .sort((a, b) => b.value - a.value);
  };

  const getLifecycleStageData = () => {
    if (!currentCategory) return [];
    return Object.entries(currentCategory.by_lifecycle_stage || {})
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({
        name: name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        value: parseFloat(value.toFixed(4)),
        percentage: currentCategory.total_value > 0 
          ? ((value / currentCategory.total_value) * 100).toFixed(1) 
          : 0
      }))
      .sort((a, b) => b.value - a.value);
  };

  // Radar chart data for multi-category comparison
  const getRadarData = () => {
    if (!lciaResult?.impact_categories) return [];
    
    // Normalize values for radar chart (0-100 scale)
    const maxValues = {};
    Object.entries(impactCategories).forEach(([id, cat]) => {
      maxValues[id] = cat.total_value;
    });
    
    const maxVal = Math.max(...Object.values(maxValues), 1);
    
    return Object.entries(impactCategories).map(([id, cat]) => ({
      category: cat.name.split(' ')[0], // Short name
      fullName: cat.name,
      value: maxVal > 0 ? (cat.total_value / maxVal) * 100 : 0,
      actualValue: cat.total_value,
      unit: cat.unit
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="phase3-lcia">
      {/* Phase Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-xl ${isLocked ? 'bg-secondary/20' : 'bg-primary/20'}`}>
            {isLocked ? <Lock className="h-6 w-6 text-secondary" /> : <BarChart3 className="h-6 w-6 text-primary" />}
          </div>
          <div>
            <h2 className="text-2xl font-bold">ISO Phase 3 – Impact Assessment (LCIA)</h2>
            <p className="text-muted-foreground">Calculate environmental impact indicators</p>
          </div>
        </div>
        <Badge variant={isLocked ? "secondary" : "outline"} className="text-sm">
          {isLocked ? 'Locked' : lciaResult ? 'Calculated' : 'Pending'}
        </Badge>
      </div>

      {/* No AI Notice */}
      <Alert className="border-primary/50 bg-primary/10">
        <AlertTriangle className="h-4 w-4 text-primary" />
        <AlertTitle>Pure Calculation Phase</AlertTitle>
        <AlertDescription>
          No AI interpretation in Phase 3. Results are numeric calculations based on selected LCIA method and characterization factors.
        </AlertDescription>
      </Alert>

      {/* Method Selection & Run */}
      {!lciaResult && project.phase_statuses['2'] === 'locked' && (
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-primary" />
              LCIA Method Selection
            </CardTitle>
            <CardDescription>Select impact assessment method - determines which impact categories are calculated</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">LCIA Method</label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="lcia-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(methods).filter(([, m]) => !m.legacy).map(([id, m]) => (
                    <SelectItem key={id} value={id}>
                      <div className="flex flex-col">
                        <span className="flex items-center gap-2">
                          {m.name}
                          {coverage?.[id] && (
                            <span className="text-xs text-muted-foreground">
                              · {coverage[id].overall_pct}% coverage
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {methodCategoryKeys(m).length} impact categories · {m.data_basis === 'reference' ? 'reference data (BAFU)' : 'generic factors'}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                  {Object.entries(methods).filter(([, m]) => m.legacy).map(([id, m]) => (
                    <SelectItem key={id} value={id}>
                      <div className="flex flex-col">
                        <span className="flex items-center gap-2">
                          {m.name}
                          <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-600">Legacy</Badge>
                        </span>
                        <span className="text-xs text-muted-foreground">{methodCategoryKeys(m).length} impact categories · generic built-in factors</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {methods[method] && (
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-sm font-medium mb-2">Impact Categories in {methods[method].name}:</p>
                <div className="flex flex-wrap gap-2">
                  {methodCategoryKeys(methods[method]).map((id) => (
                    <Badge key={id} variant="outline" className="text-xs">
                      {methods[method].category_details?.[id]?.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {selectedMethodCoverage && selectedMethodCoverage.overall_pct < 100 && (
              <Alert className="border-amber-400/60 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertTitle>Partial data coverage ({selectedMethodCoverage.overall_pct}%)</AlertTitle>
                <AlertDescription>
                  {selectedMethodCoverage.uncovered_items?.length} of {selectedMethodCoverage.items_total} inventory
                  item(s) have no data for this method and would contribute zero:{' '}
                  {selectedMethodCoverage.uncovered_items?.slice(0, 5).map(u => u.name).join(', ')}
                  {selectedMethodCoverage.uncovered_items?.length > 5 && ' …'}
                  <label className="flex items-center gap-2 mt-2 text-sm">
                    <input
                      type="checkbox"
                      checked={requireFullCoverage}
                      onChange={(e) => setRequireFullCoverage(e.target.checked)}
                      data-testid="require-full-coverage"
                    />
                    Require full coverage (fail the run instead of computing a partial result)
                  </label>
                </AlertDescription>
              </Alert>
            )}

            <Button onClick={runLCIA} disabled={running} className="w-full" data-testid="run-lcia-btn">
              <Play className="h-4 w-4 mr-2" />
              {running ? 'Calculating all impact categories...' : 'Run LCIA Calculation'}
            </Button>
          </CardContent>
        </Card>
      )}

      {project.phase_statuses['2'] !== 'locked' && !lciaResult && (
        <Alert className="border-accent/50 bg-accent/10">
          <Lock className="h-4 w-4 text-accent" />
          <AlertDescription>Phase 2 (Inventory) must be locked before running LCIA calculation.</AlertDescription>
        </Alert>
      )}

      {/* Results */}
      {lciaResult && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
          
          {/* Method Info */}
          <div className="flex items-center justify-between">
            <div>
              <Badge className="mb-2">{lciaResult.method_name || lciaResult.method}</Badge>
              {resultMethodMeta.legacy && (
                <Badge variant="outline" className="mb-2 ml-2 border-amber-400 text-amber-600">Legacy method</Badge>
              )}
              <p className="text-xs text-muted-foreground">Version: {lciaResult.method_version}</p>
            </div>
            <p className="text-xs text-muted-foreground">
              {new Date(lciaResult.created_at).toLocaleString()}
            </p>
          </div>

          {/* Coverage warning */}
          {resultCoverage && resultCoverage.overall_pct < 100 && (
            <Alert className="border-amber-400/60 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertTitle>
                {resultCoverage.items_covered} of {resultCoverage.items_total} items covered ({resultCoverage.overall_pct}%)
              </AlertTitle>
              <AlertDescription>
                Items with no data for this method contributed zero:{' '}
                {(resultCoverage.uncovered_items || []).slice(0, 8).map(u => u.name).join(', ')}
                {(resultCoverage.uncovered_items || []).length > 8 && ' …'}
              </AlertDescription>
            </Alert>
          )}

          {/* Single-score hero (Ecological Scarcity: Total UBP) */}
          {isSingleScore && totalCategory && (
            <Card className="glass-card border-primary/40">
              <CardContent className="py-6 text-center">
                <p className="text-sm text-muted-foreground mb-1">{totalCategory.name}</p>
                <p className="text-4xl font-bold font-mono text-primary">
                  {totalCategory.total_value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </p>
                <p className="text-sm text-muted-foreground mt-1">{totalCategory.unit} (eco-points, weighted single score)</p>
              </CardContent>
            </Card>
          )}

          {/* Impact Categories Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {categoryList.map(([catId, cat], idx) => {
              const Icon = categoryIcons[catId] || BarChart3;
              const isSelected = selectedCategory === catId;
              return (
                <motion.div
                  key={catId}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <button
                    onClick={() => setSelectedCategory(catId)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      isSelected 
                        ? 'border-primary bg-primary/10 shadow-lg shadow-primary/20' 
                        : 'border-border hover:border-primary/50 bg-card'
                    }`}
                    data-testid={`category-${catId}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className={`h-4 w-4 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                      <span className="text-xs text-muted-foreground truncate">{cat.name}</span>
                    </div>
                    <p className="text-xl font-bold font-mono">
                      {cat.total_value.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-muted-foreground">{cat.unit}</p>
                  </button>
                </motion.div>
              );
            })}
          </div>

          {/* Detailed View Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-3">
              <TabsTrigger value="overview">Category Detail</TabsTrigger>
              <TabsTrigger value="comparison">Multi-Category</TabsTrigger>
              <TabsTrigger value="processes">Top Processes</TabsTrigger>
            </TabsList>

            {/* Category Detail Tab */}
            <TabsContent value="overview" className="space-y-4">
              {currentCategory && (
                <>
                  <Card className="glass-card">
                    <CardHeader>
                      <CardTitle className="text-lg">{currentCategory.name}</CardTitle>
                      <CardDescription>
                        Total: {currentCategory.total_value.toLocaleString(undefined, { maximumFractionDigits: 4 })} {currentCategory.unit}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* By Inventory Category */}
                        <div>
                          <h4 className="font-medium mb-3">By Inventory Category</h4>
                          <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={getInventoryCategoryData()} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                                <YAxis dataKey="name" type="category" stroke="hsl(var(--muted-foreground))" fontSize={12} width={80} />
                                <Tooltip 
                                  contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                                  formatter={(value) => [`${value} ${currentCategory.unit}`, 'Impact']}
                                />
                                <Bar dataKey="value" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>

                        {/* By Lifecycle Stage */}
                        <div>
                          <h4 className="font-medium mb-3">By Lifecycle Stage</h4>
                          <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={getLifecycleStageData()}
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={50}
                                  outerRadius={90}
                                  paddingAngle={2}
                                  dataKey="value"
                                >
                                  {getLifecycleStageData().map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                  ))}
                                </Pie>
                                <Tooltip 
                                  contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                                  formatter={(value) => [`${value} ${currentCategory.unit}`, 'Impact']}
                                />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                          <div className="flex flex-wrap gap-2 justify-center mt-2">
                            {getLifecycleStageData().map((entry, idx) => (
                              <div key={entry.name} className="flex items-center gap-1 text-xs">
                                <div className="w-3 h-3 rounded" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                                <span>{entry.name}: {entry.percentage}%</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </TabsContent>

            {/* Multi-Category Comparison Tab */}
            <TabsContent value="comparison" className="space-y-4">
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle>{isSingleScore ? 'Eco-point Breakdown' : 'Impact Category Comparison'}</CardTitle>
                  <CardDescription>
                    {isSingleScore
                      ? 'Sub-indicator contributions in UBP — directly comparable, summing to the total score'
                      : 'Normalized view across all calculated impact categories'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-96">
                    <ResponsiveContainer width="100%" height="100%">
                      {isSingleScore ? (
                        // A normalized radar is meaningless for a pre-weighted
                        // single-score method; UBP values share one unit.
                        <BarChart data={getSubIndicatorData()} layout="vertical" margin={{ left: 40 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                          <YAxis dataKey="name" type="category" stroke="hsl(var(--muted-foreground))" fontSize={10} width={170} />
                          <Tooltip
                            contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                            formatter={(value) => [`${Number(value).toLocaleString()} UBP`, 'Impact']}
                          />
                          <Bar dataKey="value" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      ) : (
                        <RadarChart data={getRadarData()}>
                          <PolarGrid stroke="hsl(var(--border))" />
                          <PolarAngleAxis dataKey="category" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} />
                          <PolarRadiusAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} />
                          <Radar name="Impact" dataKey="value" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.5} />
                          <Tooltip
                            contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                            formatter={(value, name, props) => [
                              `${props.payload.actualValue?.toFixed(4)} ${props.payload.unit}`,
                              props.payload.fullName
                            ]}
                          />
                        </RadarChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Summary Table */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle>All Impact Categories</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Impact Category</TableHead>
                        <TableHead className="text-right">Total Value</TableHead>
                        <TableHead>Unit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categoryList.map(([catId, cat]) => (
                        <TableRow key={catId} className={selectedCategory === catId ? 'bg-primary/10' : ''}>
                          <TableCell className="font-medium">{cat.name}</TableCell>
                          <TableCell className="text-right font-mono">
                            {cat.total_value.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                          </TableCell>
                          <TableCell className="text-muted-foreground">{cat.unit}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Top Processes Tab */}
            <TabsContent value="processes" className="space-y-4">
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle>Top Contributing Processes</CardTitle>
                  <CardDescription>
                    Sorted by {isSingleScore ? 'total eco-points (UBP)' : 'climate change impact'} (primary indicator)
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">#</TableHead>
                        <TableHead>Process</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead className="text-right">
                          {isSingleScore ? 'Total (UBP)' : 'GWP (kg CO₂e)'}
                        </TableHead>
                        <TableHead className="text-right">Contribution</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(lciaResult.contributions_by_process || []).map((proc, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono text-muted-foreground">{idx + 1}</TableCell>
                          <TableCell className="font-medium">{proc.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">{proc.category}</Badge>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {(
                              (isSingleScore ? proc.primary_impact : proc.climate_change_impact)
                              ?? proc.climate_change_impact ?? proc.impact ?? 0
                            ).toLocaleString(undefined, { maximumFractionDigits: 4 })}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                                <div className="h-full bg-primary" style={{ width: `${Math.min(proc.percentage, 100)}%` }} />
                              </div>
                              <span className="font-mono text-sm w-12 text-right">{proc.percentage.toFixed(1)}%</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Metadata */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileCheck className="h-5 w-5 text-primary" />
                Calculation Metadata
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Method</p>
                  <p className="font-medium">{lciaResult.method_name || lciaResult.method}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Version</p>
                  <p className="font-medium">{lciaResult.method_version}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Impact Categories</p>
                  <p className="font-medium">{categoryList.length}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Calculated</p>
                  <p className="font-medium">{new Date(lciaResult.created_at).toLocaleString()}</p>
                </div>
                {resultCoverage && (
                  <div>
                    <p className="text-muted-foreground">Data Coverage</p>
                    <p className="font-medium">
                      {resultCoverage.overall_pct}% ({resultCoverage.items_covered}/{resultCoverage.items_total} items)
                    </p>
                  </div>
                )}
                {resultCoverage?.libraries?.length > 0 && (
                  <div>
                    <p className="text-muted-foreground">Reference Libraries</p>
                    <p className="font-medium">{resultCoverage.libraries.join(', ')}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Unlock Button when locked */}
          {isLocked && (
            <div className="flex justify-end">
              <Button 
                variant="outline"
                onClick={handleUnlock} 
                disabled={unlocking}
                className="border-amber-500 text-amber-600 hover:bg-amber-50"
                data-testid="unlock-phase3-btn"
              >
                {unlocking ? (
                  <>
                    <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-amber-500 border-t-transparent" />
                    Unlocking...
                  </>
                ) : (
                  <>
                    <Unlock className="h-4 w-4 mr-2" />
                    Unlock & Re-run LCIA
                  </>
                )}
              </Button>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}
